// features/customer/components/TenantDashboard.tsx
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import type { RootState } from "../../app/store";
import CustomerUserManagement from "./CustomerUserManagement";
import CommercialManagerDashboard from "./CommercialManagerDashboard";
import GuestAccessManager from "./GuestAccessManager";
import CampaignsAndEvents from "./CampaignsAndEvents";
import AssetIngestion from "./AssetIngestion";
import { API_ROOT_URL } from "../../config/api";
import AssetsGallery from "./AssetsGallery";
import CustomerAdminDashboard from "../tenant/CustomerAdminDashboard";

type TabType =
  | "campaigns"
  | "users"
  | "commercial"
  | "guest"
  | "assets"
  | "assets-list"
  | "dashboard"
  | "campaigns-only"
  | "events-only"
  | "products-only";

const TenantDashboard: React.FC = () => {
  const currentTenant = useSelector(
    (state: RootState) => state.tenant.currentTenant,
  );
  const [activeTab, setActiveTab] = useState<TabType>("dashboard");
  const userRole = useSelector((state: RootState) => state.auth.user?.role);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const validTabs = [
      "campaigns",
      "users",
      "commercial",
      "guest",
      "assets",
      "assets-list",
      "dashboard",
      "campaigns-only",
      "events-only",
      "products-only",
    ];
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam as TabType);
    } else if (!tabParam) {
      setActiveTab("dashboard");
    }
  }, [searchParams]);

  if (!currentTenant) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-2xl font-semibold text-gray-800">
          No tenant found
        </h2>
      </div>
    );
  }

  const visibleTabs: { id: TabType; label: string; icon: string }[] = [
    { id: "campaigns", label: "Campaigns & Events", icon: "📢" },
    { id: "assets", label: "Asset Ingestion", icon: "📤" },
    { id: "users", label: "User Management", icon: "👥" },
    { id: "commercial", label: "Financial & Billing", icon: "💰" },
    { id: "guest", label: "Guest Access", icon: "🔗" },
  ];

  const handleUploadComplete = (uploadedFiles: any[]) => {
    console.log("Upload complete in tenant dashboard:", uploadedFiles);
    // You can show a success toast or notification here
  };

  const handleUploadError = (error: Error, file?: any) => {
    console.error("Upload error in tenant dashboard:", error, file);
    // You can show an error toast or notification here
  };

  const handleUploadProgress = (progress: number, file?: any) => {
    console.log(`Upload progress: ${progress}%`, file?.name);
  };

  return (
    <div className="p-6">
      {/* Header — only for non-admin roles; CustomerAdminDashboard renders its own header */}
      {activeTab === "dashboard" && userRole?.toLowerCase() !== "customer_admin" && (
        <div className="flex items-center justify-between mb-10 bg-white rounded-2xl border border-slate-200 shadow-sm px-8 py-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900">
              Tenant Dashboard
            </h1>

            <p className="text-slate-500 mt-2 text-sm leading-6 max-w-xl">
              Overview of your campaigns, products, and events
            </p>
          </div>
        </div>
      )}

      {/* Tab Navigation
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex gap-4 flex-wrap">
                    {visibleTabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === tab.id
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
                                }`}
                        >
                            <span className="text-lg">{tab.icon}</span>
                            {tab.label}
                            {tab.id === 'assets' && (
                                <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                                    New
                                </span>
                            )}
                        </button>
                    ))}
                </nav>
            </div> */}

      {/* Role-based Access Indicator — only on the dashboard landing tab, hidden for customer_admin (CustomerAdminDashboard shows its own welcome) */}
      {activeTab === "dashboard" && userRole?.toLowerCase() !== "customer_admin" && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm">👤 Current Role:</span>
              {(() => {
                const normalizedRole = userRole?.toLowerCase();
                const roleLabels: Record<string, string> = {
                  customer_admin: "Administrator",
                  customer_manager: "Manager",
                  customer_viewer: "Viewer",
                };
                const roleColors: Record<string, string> = {
                  customer_admin: "bg-purple-100 text-purple-700",
                  customer_manager: "bg-blue-100 text-blue-700",
                  customer_viewer: "bg-gray-100 text-gray-700",
                };

                const formatFallback = (role?: string) =>
                  role
                    ? role
                        .replace(/_/g, " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (c) => c.toUpperCase())
                    : "Not logged in";

                const label = normalizedRole
                  ? roleLabels[normalizedRole] || formatFallback(userRole)
                  : "Not logged in";
                const colorClass = normalizedRole
                  ? roleColors[normalizedRole] || "bg-gray-100 text-gray-700"
                  : "bg-gray-100 text-gray-700";

                return (
                  <span
                    className={`px-2 py-1 text-xs rounded-full font-medium ${colorClass}`}
                  >
                    {label}
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Dashboard landing — summary only, no tab switcher, no create/edit UI */}
      {activeTab === "dashboard" && userRole?.toLowerCase() === "customer_admin" && (
        <CustomerAdminDashboard tenant={currentTenant} />
      )}

      {activeTab === "dashboard" && userRole?.toLowerCase() !== "customer_admin" && (
        <CampaignsAndEvents tenant={currentTenant} view="summary" />
      )}

      {/* Campaigns & Events Tab Content (Customer Admin — full tabbed view, unchanged) */}
      {activeTab === "campaigns" && (
        <CampaignsAndEvents tenant={currentTenant} />
      )}

      {/* Customer Manager — single-purpose views, no inner tab switcher */}
      {activeTab === "campaigns-only" && (
        <CampaignsAndEvents tenant={currentTenant} view="campaigns" />
      )}
      {activeTab === "events-only" && (
        <CampaignsAndEvents tenant={currentTenant} view="events" />
      )}
      {activeTab === "products-only" && (
        <CampaignsAndEvents tenant={currentTenant} view="products" />
      )}

      {/* Asset Ingestion Tab Content */}
      {activeTab === "assets" && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="text-4xl">📤</div>
              <div>
                <h2 className="text-xl font-bold mb-2">
                  Asset Ingestion Pipeline
                </h2>
                <p className="text-blue-100">
                  Upload assets to be processed by the ingestion workflow.
                  Supported formats include images, videos, documents, and more.
                  Files will be automatically validated and scanned for malware.
                </p>
              </div>
            </div>
          </div>

          <AssetIngestion
            userId={currentUser?.id}
            userEmail={currentUser?.email}
            workspaceId={String(currentTenant.id)}
            onUploadComplete={handleUploadComplete}
            onUploadError={handleUploadError}
            onProgress={handleUploadProgress}
            autoStart={false}
            showHistory={true}
            showMetadata={true}
            allowMultiple={true}
            enableChunking={false}
            // Updated endpoint to match the backend route
            uploadEndpoint={`${API_ROOT_URL}/v1/assets/ingest`}
            processEndpoint={`${API_ROOT_URL}/v1/assets/process`}
            jobsEndpoint={`${API_ROOT_URL}/v1/assets/jobs`}
            config={{
              allowedFileTypes: {
                "image/jpeg": ".jpg, .jpeg",
                "image/png": ".png",
                "image/gif": ".gif",
                "image/webp": ".webp",
                "image/svg+xml": ".svg",
                "video/mp4": ".mp4",
                "video/quicktime": ".mov",
                "video/x-msvideo": ".avi",
                "video/mpeg": ".mpeg",
                "video/webm": ".webm",
                "audio/mpeg": ".mp3",
                "audio/wav": ".wav",
                "audio/ogg": ".ogg",
                "application/pdf": ".pdf",
                "application/msword": ".doc",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                  ".docx",
                "application/vnd.ms-excel": ".xls",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  ".xlsx",
                "text/plain": ".txt",
                "application/json": ".json",
                "application/zip": ".zip",
              },
              maxFileSize: 10 * 1024 * 1024 * 1024, // 10GB
              maxTotalSize: 50 * 1024 * 1024 * 1024, // 50GB
              maxConcurrentUploads: 3,
              enableRetry: true,
              maxRetries: 3,
              retryDelay: 2000,
              enableDownstreamProcessing: false,
              enableThumbnailGeneration: false,
              chunkSize: 10 * 1024 * 1024,
              useChunkedUpload: false,
              enableResumableUploads: false,
            }}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="text-3xl mb-3">🔒</div>
              <h3 className="font-semibold text-gray-800 mb-2">
                Secure Uploads
              </h3>
              <p className="text-sm text-gray-600">
                All files are encrypted during transmission and at rest. Access
                is controlled through your tenant's security policies.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="text-3xl mb-3">🛡️</div>
              <h3 className="font-semibold text-gray-800 mb-2">
                Malware Scanning
              </h3>
              <p className="text-sm text-gray-600">
                Files are automatically scanned for malware and viruses before
                being uploaded to secure storage.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="text-3xl mb-3">📊</div>
              <h3 className="font-semibold text-gray-800 mb-2">
                Analytics & Tracking
              </h3>
              <p className="text-sm text-gray-600">
                Track upload history, processing status, and usage analytics for
                all your organization's assets.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Assets Gallery Tab Content — read-only list, NEW */}
      {activeTab === "assets-list" && <AssetsGallery />}

      {/* User Management Tab Content */}
      {activeTab === "users" && (
        <CustomerUserManagement tenantId={String(currentTenant.id)} />
      )}

      {/* Commercial Manager Tab Content */}
      {activeTab === "commercial" && (
        <CommercialManagerDashboard tenantId={String(currentTenant.id)} />
      )}

      {/* Guest Access Tab Content */}
      {activeTab === "guest" && (
        <GuestAccessManager tenantId={currentTenant.id} />
      )}
    </div>
  );
};

export default TenantDashboard;
